/**
 * RU — основная локаль. Первичный источник строк интерфейса.
 *
 * Правила:
 *   - Здесь живут ТОЛЬКО короткие строки (CTA, валидация, тосты, aria, нав-пункты).
 *   - Длинные тексты (позиционирование, модули, кейсы) — в `@/content` и
 *     передаются компонентам пропсами.
 *   - Исходник микрокопии: `content/13-ui-microcopy.md`.
 *
 * Структура зеркалится в `en.ts` (см. `en.ts` — строки `TODO_EN`).
 * Тип — `ru` сам по себе; `types.ts` экспортирует `type MessageSchema = typeof ru`.
 */

const ru = {
  common: {
    brand: 'Bafsy',
    tagline: 'Промо-платформа нового поколения',
  },
  nav: {
    home: 'Главная',
    platform: 'Платформа',
    solutions: 'Решения',
    workflow: 'Как мы работаем',
    cases: 'Кейсы',
    pricing: 'Цены',
    calculator: 'Калькулятор',
    brief: 'Бриф',
    contact: 'Контакты',
    privacy: 'Политика конфиденциальности',
    cta: 'Оставить заявку',
    menuOpen: 'Меню',
    menuClose: 'Закрыть',
    anchors: {
      composition: 'Возможности',
      cases: 'Кейсы',
      templates: 'Шаблоны',
      workflow: 'Как работаем',
      pricing: 'Цены',
      contact: 'Контакты',
    },
  },
  cta: {
    start: 'Начать',
    startBrief: 'Заполнить бриф',
    calcEstimate: 'Рассчитать смету',
    moreInfo: 'Подробнее',
    details: 'Подробнее',
    showAll: 'Показать все',
    showLess: 'Свернуть',
    reset: 'Сбросить',
    tryCalculator: 'Открыть калькулятор',
    requestDemo: 'Записаться на демо',
    downloadWhitepaper: 'Получить материал',
    getCaseBook: 'Получить книгу кейсов',
    getPresentation: 'Получить презентацию',
    tryBuilder: 'Попробовать',
    bookSlot: 'Забронировать место',
    bookVideoDemo: 'Записаться на видео-презентацию',
    sendPresentation: 'Прислать презентацию на почту',
    subscribeNews: 'Подписаться на новости',
    submitLead: 'Оставить заявку',
    backToHome: 'На главную',
    writeUs: 'Написать нам',
    sendForm: 'Отправить',
    sending: 'Отправляем…',
  },
  form: {
    submit: 'Отправить',
    submitting: 'Отправляем…',
    label: {
      name: 'Имя',
      email: 'E-mail',
      phone: 'Телефон',
      company: 'Компания',
      message: 'Сообщение',
      site: 'Сайт компании',
      callTime: 'Удобное время звонка',
    },
    placeholder: {
      email: "name{'@'}company.ru",
      phone: '+7 (999) 000-00-00',
      company: 'ООО «Компания»',
    },
    fields: {
      name: 'Имя',
      email: 'E-mail',
      phone: 'Телефон',
      company: 'Компания',
      message: 'Сообщение',
      messagePlaceholder: 'Коротко опишите задачу или сроки',
    },
    consent: {
      label: 'Я принимаю условия политики конфиденциальности',
    },
    httpsNote: 'Ваши данные защищены и будут переданы по безопасному протоколу https',
    notOffer: 'Предложение не является публичной офертой',
    optional: 'необязательно',
    validation: {
      required: 'Пожалуйста, заполните это поле',
      email: "Введите корректный email, например name{'@'}company.ru",
      phone: 'Введите телефон в формате +7 (999) 000-00-00',
      consent: 'Чтобы отправить форму, примите условия политики конфиденциальности',
      minLength: 'Минимум {n} символов',
      maxLength: 'Не больше {n} символов',
      submitFailed: 'Не получилось отправить — попробуйте ещё раз или напишите нам напрямую',
    },
  },
  validation: {
    required: 'Пожалуйста, заполните это поле',
    email: "Введите корректный email, например name{'@'}company.ru",
    phone: 'Введите телефон в формате +7 (999) 000-00-00',
    consent: 'Чтобы отправить форму, примите условия политики конфиденциальности',
    minLen: 'Минимум {n} символов',
    maxLen: 'Не больше {n} символов',
    generic: 'Не получилось отправить — попробуйте ещё раз или напишите нам напрямую',
  },
  toast: {
    success: {
      lead: 'Заявка отправлена, мы свяжемся с вами в течение рабочего дня',
      caseBook: 'Книга кейсов отправлена на {email}. Проверьте почту (и папку «Спам»).',
    },
    error: {
      network: 'Нет соединения. Проверьте интернет и повторите попытку',
      generic: 'Что-то пошло не так. Попробуйте ещё раз через минуту',
    },
    newsletter: {
      pending: 'Спасибо! Подписка будет активна после запуска рассылки.',
    },
    lead: {
      success: 'Заявка отправлена, мы свяжемся с вами в течение рабочего дня',
      error: 'Не получилось отправить — попробуйте ещё раз или напишите нам напрямую',
      pending: 'Отправляем…',
    },
  },
  modal: {
    thanksTitle: 'Спасибо за оказанное доверие!',
    thanksBody: 'Мы свяжемся с вами в ближайшее время.',
    officeHours: 'Время работы офиса — пн–пт, 10:00–19:00 по Москве',
    exitIntentTitle: 'Постойте! Не уходите с пустыми руками!',
    exitIntentBody:
      'Оставьте заявку и получите нашу книгу кейсов со ссылками на полные версии сайтов.',
  },
  cookie: {
    banner: {
      text: 'Мы используем куки, чтобы сайт работал и было удобно его использовать. Подробности — в политике конфиденциальности.',
    },
    accept: 'Принять',
    essential: 'Только необходимые',
    configure: 'Настроить',
    drawer: {
      title: 'Настройки cookie',
      description:
        'Управляйте категориями cookie. Строго необходимые куки нужны для работы сайта и не отключаются.',
      save: 'Сохранить настройки',
    },
    categories: {
      essential: 'Строго необходимые',
      essentialDescription:
        'Необходимы для работы сайта (сессия, роутинг, язык). Нельзя отключить.',
      analytics: 'Аналитика',
      analyticsDescription:
        'Помогают понять, как пользователи взаимодействуют с сайтом. Данные обезличены.',
      marketing: 'Маркетинг',
      marketingDescription:
        'Используются для показа релевантной рекламы и ретаргетинга на внешних площадках.',
    },
  },
  notFound: {
    heading: 'Страница не найдена',
    body: 'Возможно, мы переименовали её, либо вы перешли по старой ссылке.',
  },
  calculator: {
    heading: 'Калькулятор сметы',
    next: 'Далее',
    back: 'Назад',
    submit: 'Получить результаты',
    saveAsPdf: 'Сохранить как PDF',
    resume: 'Продолжить с сохранённого шага',
    startOver: 'Начать заново',
    resetConfirm: 'Сбросить все выбранные опции и начать заново?',
    totalLabel: 'Предварительная стоимость',
    currency: '₽',
    currencyNote: '',
    stepCounter: 'Шаг {current} из {total}',
    draftSaved: 'Черновик сохранён',
    hint: 'Итоговая цена — ориентировочная. Зафиксируем после брифинга.',
    resumedToast: 'Продолжаем с сохранённого шага. Можно начать заново — в меню.',
    submittedToast: 'Смета отправлена на {email}. В течение дня пришлём PDF со всеми расчётами.',
    pdfToast: 'PDF со сметой будет подключён в следующей версии.',
    formTitle: 'Как с вами связаться?',
    formEmailLabel: 'E-mail',
    formPhoneLabel: 'Телефон',
  },
  brief: {
    heading: 'Бриф «Давайте знакомиться!»',
    next: 'Далее',
    back: 'Назад',
    skip: 'Пропустить',
    submit: 'Получить результат',
    resume: 'Продолжить заполнение',
    resetConfirm: 'Сбросить ответы и начать заново?',
    stepCounter: 'Шаг {current} из {total}',
    draftSaved: 'Черновик сохранён',
    resumedToast: 'Продолжаем с сохранённого шага. Можно начать заново — в меню.',
    submittedToast: 'Спасибо! Презентации придут на {email} в течение рабочего дня.',
    customOptionLabel: 'Свой вариант (можно дополнить текст)',
    formTitle: 'Как с вами связаться?',
    formEmailLabel: 'E-mail',
  },
  aria: {
    skipToMain: 'К основному содержимому',
    langSwitcher: 'Выбрать язык сайта',
    scrollToTop: 'Наверх страницы',
    closeModal: 'Закрыть окно',
    dismissToast: 'Закрыть уведомление',
    openMenu: 'Открыть меню',
    closeMenu: 'Закрыть меню',
    openDropdown: 'Развернуть меню',
    loading: 'Идёт загрузка',
    breadcrumbs: 'Хлебные крошки',
    promo: 'Промо-сообщение',
    cookieConsent: 'Согласие на использование cookie',
    cookieAnalytics: 'Переключить аналитические cookie',
    cookieMarketing: 'Переключить маркетинговые cookie',
    pricingTotal: 'Предварительная сумма сметы',
    workflowSteps: 'Шаги работы над проектом',
    subproductSections: 'Разделы страницы продукта',
  },
  breadcrumbs: {
    home: 'Главная',
  },
  footer: {
    tagline: 'Платформа для запуска промо-кампаний любой сложности.',
    copyright: '© {year} ООО «Бафси». Все права защищены.',
    legalEntity: 'ООО «Бафси»',
    address: 'г. Москва, ул. Талалихина, д. 41, стр. 9, помещ. 1/4',
    hours: 'Пн–Пт, 10:00–19:00 по Москве',
    sections: {
      products: 'Продукты',
      company: 'Компания',
      legal: 'Правовое',
    },
    links: {
      blogSoon: 'Блог (скоро)',
      contacts: 'Контакты',
      accelerator: 'Акселератор',
    },
    newsletter: {
      cta: 'Подписаться на новости',
      placeholder: "name{'@'}company.ru",
      heading: 'Подпишитесь на наши новости',
      description: 'Идеи, кейсы и инструменты для ярких промо — раз в месяц.',
    },
  },
  promo: {
    ideasTelegram: 'Идеи, кейсы и инструменты для ярких промо — подписаться',
    fmcgTelegram: 'Вся правда об FMCG, актуальные новости, факты о производстве',
    infoPartner: 'Инфопартнёр',
    dismiss: 'Закрыть промо',
  },
  status: {
    ready: 'Готово',
    beta: 'Beta',
    planned: 'Планируется',
  },
  sections: {
    hero: {
      eyebrow: 'Платформа для запуска промо-акций',
      heading: 'Оптимизируем стоимость и скорость запуска промо-акций до 4-х раз',
      lead: 'Запуск промо-акций на платформе Bafsy. Платформа позволяет запускать промо-кампании любой сложности и конфигурации под ключ.',
      primaryCta: 'Заполнить бриф',
      secondaryCta: 'Посмотреть 3-минутное видео',
    },
    trust: {
      title: 'За нами — цифры, сертификаты и патент',
      subtitle:
        'Мы запустили более 220 акций со сложной механикой, сократили время запуска на 60 % и получили чек-лист качества из 63 пунктов.',
      logosLabel: 'Нам доверяют 249+ клиентов',
    },
    proof: {
      title: 'Нам доверяют',
      subtitle: 'Более 249 клиентов запускали промо-кампании на Bafsy.',
    },
    platform: {
      title: 'Bafsy состоит из четырёх элементов',
      subtitle:
        'Платформа собрана из 14 готовых решений, движка PromoKit, визуального конструктора и готовых интеграций.',
      showcase: {
        title: 'Платформа под капотом: PromoKit внутри Bafsy',
        subtitle:
          '16 реальных экранов CRM, конструктора и операций — на них мы собирали 300+ промо-кампаний для FMCG.',
        intro:
          'Тур начинается с выбора типа акции и шагов быстрого запуска, проходит через конструкторы анкеты, призов, писем и чеков, задерживается на геймификации и заканчивается мультиканальным запуском в лендинг, сайт и Telegram-бот.',
        groupAll: 'Все',
        groupBuilder: 'Конструктор',
        groupCrm: 'CRM',
        groupOperations: 'Процессы',
        groupOther: 'Другое',
        next: 'Следующий экран',
        prev: 'Предыдущий экран',
        source: 'Источник',
      },
    },
    solutions: {
      title: 'Выберите один из 14 готовых инструментов',
      subtitle: 'Подключите готовое решение или закажите разработку нового под вашу механику.',
      cardNotFound: {
        title: 'Хотите индивидуальный спецпроект?',
        subtitle: 'Оставьте заявку и мы разработаем для вас уникальное предложение',
      },
    },
    modules: {
      title: 'Подключим функциональные модули движка PromoKit',
      subtitle: 'Не нужно платить за всё — выбирайте только нужные модули.',
    },
    visualBuilder: {
      title: 'Визуальный конструктор',
      subtitle: 'Соберите сайт промо-акции без разработчиков и программистов.',
    },
    integrations: {
      title: 'Интеграции и размещение',
      subtitle: 'Bafsy встаёт в вашу инфраструктуру — облачно или on-premise.',
      outcomesTitle: 'Что получает клиент',
    },
    workflow: {
      title: 'Полный цикл работ за 6 этапов',
      subtitle: 'От заполнения брифа до обслуживания запущенной кампании.',
      receivesLabel: 'Клиент получает',
    },
    cases: {
      eyebrow: 'Кейсы',
      title: 'Мы разработали более 900 промо-сайтов',
      subtitle: 'Вот несколько последних проектов платформы.',
      carousel: {
        roledescription: 'Карусель',
        label: 'Кейсы PromoKit',
        slideLabel: '{current} из {total}',
        prev: 'Предыдущий кейс',
        next: 'Следующий кейс',
        goTo: 'Перейти к кейсу {n}',
      },
    },
    templates: {
      eyebrow: 'Шаблоны',
      title: 'Готовые шаблоны промо',
      subtitle:
        'Выберите формат, который подходит задаче — и запускайте акцию за недели, а не месяцы.',
      traitsLabel: 'Что внутри',
      useCta: 'Использовать',
      timelineLabel: 'Срок',
      modalTitle: 'Запуск по шаблону «{template}»',
      modalLead: 'Расскажите о проекте — вернёмся с черновиком сметы и таймингом.',
      messagePrefix: 'Шаблон: {template}',
    },
    briefTeaser: {
      title: 'Давайте знакомиться!',
      subtitle: 'Ответьте на несколько вопросов — и мы предложим решение под ваш бизнес.',
      bonus: 'Пройдите опрос до конца и получите презентацию о платформе и презентацию с кейсами.',
    },
    pricingTeaser: {
      title: 'Контролировать расходы на проект — легко',
      subtitle:
        'Рассчитайте смету в калькуляторе и получите предварительную оценку стоимости проекта.',
      assurance: 'Цена железная, доплачивать не придётся.',
    },
    blog: {
      title: 'Блог Bafsy — скоро',
      subtitle: 'Готовим статьи о запуске промо-акций и механиках. Следите за обновлениями.',
      soonLabel: 'Скоро',
    },
    leadMagnet: {
      title: 'Получите книгу кейсов',
      subtitle: 'Оставьте e-mail — пришлём PDF со ссылками на полные версии сайтов.',
      description: 'Оставьте e-mail — пришлём PDF со ссылками на полные версии сайтов.',
      fileLabel: 'PDF · 2.5 МБ',
    },
    contact: {
      title: 'Оставьте заявку на реализацию проекта',
      subtitle: 'Забронируем слот в производстве и свяжемся в рабочее время.',
    },
    closing: {
      title: 'Готовы запустить промо?',
      subtitle: 'Начните с брифа или рассчитайте стоимость — решите за 5 минут.',
    },
  },
  pages: {
    home: {
      heroEyebrow: 'Промо-платформа Bafsy',
    },
    brief: {
      heading: 'Давайте знакомиться!',
      lead: '4 коротких шага, чтобы мы подобрали инструменты и подготовили презентацию под ваш бизнес.',
      heroEyebrow: 'Бриф',
    },
    calculator: {
      heading: 'Контролировать расходы на проект — легко',
      lead: 'Рассчитайте предварительную смету промо-кампании за 7 шагов и получите PDF со всеми документами.',
      heroEyebrow: 'Калькулятор сметы',
    },
    privacy: {
      heading: 'Политика конфиденциальности',
      lead: 'Юридические документы, реквизиты ООО «Бафси» и порядок обработки персональных данных.',
      content: {
        main: {
          title: 'Юридическое лицо',
          companyName: 'ООО «Бафси»',
          inn: 'ИНН',
          kpp: 'КПП',
          ogrn: 'ОГРН',
          opd: 'ОПД регистрационный номер',
          duns: 'DUNS',
        },
        address: {
          title: 'Адрес',
          subtitle: 'Юридический и фактический',
          address:
            '109316, г. Москва, вн. тер. г. муниципальный округ Нижегородский, ул. Талалихина, д. 41, стр. 9, помещ. 1/4.',
        },
        openingHours: {
          title: 'Время работы',
          openingHours: 'Пн–Пт, 10:00–19:00 по Москве',
        },
        contacts: {
          title: 'Контакты',
        },
        achievements: {
          title: 'Сертификаты и членства',
          gost: 'ГОСТ',
          labelCertificate: 'сертификат качества услуг',
          event1: {
            title: 'АРПП «Отечественный Софт»',
            labelCard: 'карточка участника',
            lableCertificate: 'сертификат',
          },
          event2: {
            title: 'Союз поддержки и развития Технологических Компаний',
          },
          event3: {
            titleBegin: 'Премия',
            title: 'Цифровое Партнёрство',
            titleEnd:
              '(октябрь 2021) в номинации «Цифровые системы для бизнеса» — выбрали из более чем 200 разработчиков России и СНГ.',
          },
        },
        legals: {
          title: 'Юридические документы',
          lebalPersonalData: 'Политика обработки персональных данных',
          lebalPolicy: 'Политика конфиденциальности',
        },
        copyright: '«© ООО «Бафси». Все права защищены.»',
      },
    },
    subproduct: {
      faqAnswerPlaceholder:
        'Расскажем подробнее на брифе или в личной переписке — оставьте заявку, и мы ответим в течение часа.',
    },
    notFound: {
      heading: 'Страница не найдена',
      lead: 'Возможно, мы переименовали её, либо вы перешли по старой ссылке.',
      body: 'Проверьте адрес или вернитесь на главную — там собраны все разделы.',
      homeCta: 'На главную',
      contactCta: 'Написать нам',
    },
  },
  seo: {
    defaults: {
      title: 'Bafsy — промо-платформа',
      description:
        'Промо-платформа Bafsy: 14 готовых решений, zero-code конструктор, модули PromoKit.',
    },
    home: {
      title: 'Платформа для запоминающихся промо-акций',
      description:
        'Оптимизируем стоимость и скорость запуска промо-акций до 4-х раз. 14 готовых решений, zero-code конструктор, модули PromoKit.',
    },
  },
};

export default ru;
export type RuMessages = typeof ru;
